// frontend/src/pages/RawData.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { authHeaders } from "../utils/authHeaders";
import "./RawData.css";
import * as XLSX from "xlsx";

const API =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.replace(/\/$/, "")) ||
  `${window.location.protocol}//${window.location.hostname}:3000/api`;

export default function RawData() {
  const { user } = useContext(AuthContext);

  const [tab, setTab] = useState("survey"); // survey | orders | selfAssessment | ao | news

  // Survey tab state
  const [surveyTitles, setSurveyTitles] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState("");
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [surveyError, setSurveyError] = useState("");
  const [surveyRows, setSurveyRows] = useState([]); // aggregated submissions
  const [surveyMeta, setSurveyMeta] = useState(null); // {survey_id,title}
  const [openSubmission, setOpenSubmission] = useState(null); // submission object
  const [editSubmission, setEditSubmission] = useState(null); // editable submission object
  const [savingEditSubmission, setSavingEditSubmission] = useState(false);
  const [editSubmissionError, setEditSubmissionError] = useState("");
  const [deletingSubmissionId, setDeletingSubmissionId] = useState(null);

  // Orders tab state
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [orders, setOrders] = useState([]);
  const [deletingOrderId, setDeletingOrderId] = useState(null);

  // Self assessment tab state
  const [selfAssessmentLoading, setSelfAssessmentLoading] = useState(false);
  const [selfAssessmentError, setSelfAssessmentError] = useState("");
  const [selfAssessmentRows, setSelfAssessmentRows] = useState([]);
  const [openSelfAssessment, setOpenSelfAssessment] = useState(null);
  const [deletingSelfAssessmentId, setDeletingSelfAssessmentId] = useState(null);
  const [selfAssessmentFilters, setSelfAssessmentFilters] = useState({
    role: "",
    timeline: "",
    type: "",
    respondent_type: "",
    q: "",
  });

  // AO tab state
  const [aoLoading, setAoLoading] = useState(false);
  const [aoError, setAoError] = useState("");
  const [aoRows, setAoRows] = useState([]);
  const [openAoRow, setOpenAoRow] = useState(null);
  const [editAoRow, setEditAoRow] = useState(null);
  const [savingAoRow, setSavingAoRow] = useState(false);
  const [editAoError, setEditAoError] = useState("");
  const [deletingAoId, setDeletingAoId] = useState(null);
  const [aoFilters, setAoFilters] = useState({
    desa: "",
    kecamatan: "",
    kabupaten: "",
    provinsi: "",
  });

  // News analytics tab state
  const [newsAnalyticsLoading, setNewsAnalyticsLoading] = useState(false);
  const [newsAnalyticsError, setNewsAnalyticsError] = useState("");
  const [newsAnalyticsRows, setNewsAnalyticsRows] = useState([]);
  const [newsMediaAccessRows, setNewsMediaAccessRows] = useState([]);
  const [newsAnalyticsSummary, setNewsAnalyticsSummary] = useState({
    page_opens: 0,
    unique_visitors: 0,
    unique_sessions: 0,
  });

  // ---- helpers
  async function asJsonOrThrow(res) {
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      const t = await res.text();
      throw new Error(`Unexpected response (not JSON): ${t.slice(0, 160)}...`);
    }
    const j = await res.json();
    if (!res.ok || !j.success) throw new Error(j?.message || `HTTP ${res.status}`);
    return j;
  }

  function formatDt(v) {
    if (!v) return "";
    try {
      return new Date(v).toLocaleString();
    } catch {
      return String(v);
    }
  }

  function parseAnswerJson(v) {
    if (v == null || String(v).trim() === "") return null;
    if (typeof v === "object") return v;
    try {
      return JSON.parse(v);
    } catch {
      return null;
    }
  }

  function resolveSurveyMediaUrl(value) {
    const raw = String(value || "").trim();
    if (!raw) return null;
    if (/^(blob:|data:)/i.test(raw)) return raw;
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith("/api/uploads/")) return `${API.replace(/\/api$/, "")}${raw}`;
    if (raw.startsWith("/uploads/")) return `${API}${raw.replace(/^\/api/, "")}`;
    return `${API}/uploads/${encodeURIComponent(raw)}`;
  }

  function getMediaUrl(a) {
    const meta = parseAnswerJson(a?.answer_json);
    const rawUrl = meta?.url || a?.answer_text || "";
    const text = String(rawUrl || "").trim();
    const type = String(a?.question_type || "").toLowerCase();
    const looksLikeUpload = /^\/?(api\/)?uploads\//i.test(text) || /^https?:\/\/.+\/(api\/)?uploads\//i.test(text);
    const looksLikeMediaFile = /\.(png|jpe?g|gif|webp|bmp|mp4|mov|webm|pdf)(\?.*)?$/i.test(text);
    if (type !== "media" && !meta?.url && !looksLikeUpload && !looksLikeMediaFile) return null;
    return resolveSurveyMediaUrl(text);
  }

  function getMediaLabel(a) {
    const meta = parseAnswerJson(a?.answer_json);
    return meta?.original_name || String(a?.answer_text || "").split("/").pop() || "Buka media";
  }

  function isGpsAnswer(a) {
    const type = String(a?.question_type || "").toLowerCase();
    const meta = parseAnswerJson(a?.answer_json);
    return (
      type === "gps" ||
      (meta &&
        Number.isFinite(Number(meta.latitude)) &&
        Number.isFinite(Number(meta.longitude)))
    );
  }

  function formatGpsAnswer(a) {
    const meta = parseAnswerJson(a?.answer_json);
    if (!meta) return "";
    const lat = Number(meta.latitude);
    const lng = Number(meta.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
    const parts = [`${lat.toFixed(6)}, ${lng.toFixed(6)}`];
    if (Number.isFinite(Number(meta.accuracy))) parts.push(`akurasi ${Math.round(Number(meta.accuracy))} m`);
    if (meta.captured_at) parts.push(`diambil ${formatDt(meta.captured_at)}`);
    return parts.join(" | ");
  }

  function normalizeAnswer(a) {
    const mediaUrl = getMediaUrl(a);
    if (mediaUrl) return mediaUrl;
    if (isGpsAnswer(a)) return formatGpsAnswer(a);
    if (a?.answer_text != null && String(a.answer_text).trim() !== "") return String(a.answer_text);
    if (a?.answer_number != null && String(a.answer_number).trim() !== "") return Number(a.answer_number);
    if (a?.answer_json != null) {
      try {
        if (typeof a.answer_json === "string") return a.answer_json;
        return JSON.stringify(a.answer_json);
      } catch {
        return String(a.answer_json);
      }
    }
    return "";
  }

  function getAnswerType(a) {
    if (a?.answer_number != null && String(a.answer_number).trim() !== "") return "number";
    if (a?.answer_json != null && String(a.answer_json).trim() !== "") return "json";
    return "text";
  }

  function getSurveyExportQuestionKey(answer) {
    const order = answer?.q_order != null ? Number(answer.q_order) : null;
    const text = String(answer?.question_text || "").trim();
    if (order != null && Number.isFinite(order) && text) return `q_${order}__${text}`;
    if (order != null && Number.isFinite(order)) return `q_${order}`;
    if (text) return `q_text__${text}`;
    return "";
  }

  function getDisplayQuestionNo(answer) {
    const order = answer?.q_order != null ? Number(answer.q_order) : null;
    if (!Number.isFinite(order)) return null;
    return order + 1;
  }

  function getSurveyExportQuestionHeader(answer) {
    const order = getDisplayQuestionNo(answer);
    const text = String(answer?.question_text || "").trim();
    if (order != null && Number.isFinite(order) && text) return `${order}. ${text}`;
    if (text) return text;
    if (order != null && Number.isFinite(order)) return `Q${order}`;
    return "Pertanyaan";
  }

  // ---- load titles (survey)
  useEffect(() => {
    if (!user?.role) return;
    (async () => {
      try {
        const res = await fetch(`${API}/raw-data/surveys/titles`, {
          headers: authHeaders(user),
          credentials: "include",
        });
        const json = await asJsonOrThrow(res);
        setSurveyTitles(json.data || []);
      } catch (e) {
        console.error(e);
        setSurveyTitles([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // ---- load orders when tab switched
  useEffect(() => {
    if (tab !== "orders" || !user?.role) return;
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user?.role]);

  useEffect(() => {
    if (tab !== "ao" || !user?.role) return;
    fetchAoData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user?.role]);

  useEffect(() => {
    if (tab !== "selfAssessment" || !user?.role) return;
    fetchSelfAssessmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user?.role]);

  useEffect(() => {
    if (tab !== "news" || !user?.role) return;
    fetchNewsAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user?.role]);

  async function fetchSurveyData(surveyId) {
    if (!surveyId) return;
    setSurveyLoading(true);
    setSurveyError("");
    setSurveyRows([]);
    setSurveyMeta(null);
    try {
      const res = await fetch(`${API}/raw-data/surveys?survey_id=${encodeURIComponent(surveyId)}`, {
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      setSurveyMeta(json.survey || null);
      setSurveyRows(json.submissions || []);
    } catch (e) {
      console.error(e);
      setSurveyError(e.message || "Gagal memuat data survey.");
    } finally {
      setSurveyLoading(false);
    }
  }

  async function fetchOrders() {
    setOrdersLoading(true);
    setOrdersError("");
    setOrders([]);
    try {
      const res = await fetch(`${API}/raw-data/orders`, {
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      setOrders(json.data || []);
      if (json?.notice) setOrdersError(String(json.notice));
    } catch (e) {
      console.error(e);
      setOrdersError(e.message || "Gagal memuat data order.");
    } finally {
      setOrdersLoading(false);
    }
  }

  async function fetchSelfAssessmentData(overrideFilters) {
    setSelfAssessmentLoading(true);
    setSelfAssessmentError("");
    setSelfAssessmentRows([]);
    try {
      const activeFilters = overrideFilters || selfAssessmentFilters;
      const params = { limit: "100000" };
      Object.entries(activeFilters || {}).forEach(([k, v]) => {
        const val = String(v ?? "").trim();
        if (val) params[k] = val;
      });
      const q = new URLSearchParams(params);
      const res = await fetch(`${API}/raw-data/self-assessments?${q.toString()}`, {
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      setSelfAssessmentRows(json.data || []);
    } catch (e) {
      console.error(e);
      setSelfAssessmentError(e.message || "Gagal memuat data self assessment.");
    } finally {
      setSelfAssessmentLoading(false);
    }
  }

  async function fetchNewsAnalytics() {
    setNewsAnalyticsLoading(true);
    setNewsAnalyticsError("");
    try {
      const res = await fetch(`${API}/raw-data/news-analytics`, {
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      setNewsAnalyticsRows(Array.isArray(json.data) ? json.data : []);
      setNewsMediaAccessRows(Array.isArray(json.media_access) ? json.media_access : []);
      setNewsAnalyticsSummary({
        page_opens: Number(json?.summary?.page_opens || 0),
        unique_visitors: Number(json?.summary?.unique_visitors || 0),
        unique_sessions: Number(json?.summary?.unique_sessions || 0),
      });
    } catch (e) {
      console.error(e);
      setNewsAnalyticsRows([]);
      setNewsMediaAccessRows([]);
      setNewsAnalyticsError(e.message || "Gagal memuat analytics berita.");
    } finally {
      setNewsAnalyticsLoading(false);
    }
  }

  function openEditSubmissionModal(submission) {
    const normalizedAnswers = (submission?.answers || []).map((a) => {
      const answerType = getAnswerType(a);
      let editValue = "";
      if (answerType === "number") editValue = String(a.answer_number ?? "");
      else if (answerType === "json") {
        try {
          editValue =
            typeof a.answer_json === "string"
              ? a.answer_json
              : JSON.stringify(a.answer_json ?? [], null, 2);
        } catch {
          editValue = "[]";
        }
      }
      else editValue = String(a.answer_text ?? "");

      return {
        ...a,
        answerType,
        editValue,
      };
    });

    setEditSubmission({
      ...submission,
      answers: normalizedAnswers,
    });
    setEditSubmissionError("");
  }

  async function saveEditedSubmission() {
    if (!editSubmission?.id || savingEditSubmission) return;

    const payloadAnswers = [];
    for (const a of editSubmission.answers || []) {
      const answerId = Number(a?.id);
      if (!Number.isFinite(answerId)) continue;

      const rawValue = String(a?.editValue ?? "");
      if (a.answerType === "number") {
        if (rawValue.trim() === "") {
          payloadAnswers.push({ answer_id: answerId, answer_number: null });
        } else {
          const n = Number(rawValue);
          if (!Number.isFinite(n)) {
            setEditSubmissionError("Ada jawaban angka yang tidak valid.");
            return;
          }
          payloadAnswers.push({ answer_id: answerId, answer_number: n });
        }
      } else if (a.answerType === "json") {
        if (rawValue.trim() === "") {
          payloadAnswers.push({ answer_id: answerId, answer_json: null });
        } else {
          let parsed;
          try {
            parsed = JSON.parse(rawValue);
          } catch {
            setEditSubmissionError("Ada jawaban JSON yang tidak valid.");
            return;
          }
          payloadAnswers.push({ answer_id: answerId, answer_json: JSON.stringify(parsed) });
        }
      } else {
        payloadAnswers.push({ answer_id: answerId, answer_text: rawValue });
      }
    }

    setSavingEditSubmission(true);
    setEditSubmissionError("");
    try {
      const res = await fetch(`${API}/raw-data/surveys/submissions/${editSubmission.id}`, {
        method: "PUT",
        headers: {
          ...authHeaders(user, { "Content-Type": "application/json" }),
        },
        credentials: "include",
        body: JSON.stringify({ answers: payloadAnswers }),
      });
      const json = await asJsonOrThrow(res);
      if (!json?.success) throw new Error(json?.message || "Gagal memperbarui jawaban.");

      await fetchSurveyData(selectedSurveyId);
      setEditSubmission(null);
    } catch (e) {
      console.error(e);
      setEditSubmissionError(e.message || "Gagal menyimpan perubahan jawaban.");
    } finally {
      setSavingEditSubmission(false);
    }
  }

  async function deleteSubmission(submission) {
    const submissionId = Number(submission?.id);
    if (!Number.isFinite(submissionId) || deletingSubmissionId === submissionId) return;

    const ok = window.confirm(`Hapus submission #${submissionId}? Tindakan ini tidak dapat dibatalkan.`);
    if (!ok) return;

    setDeletingSubmissionId(submissionId);
    setSurveyError("");
    try {
      const res = await fetch(`${API}/raw-data/surveys/submissions/${submissionId}`, {
        method: "DELETE",
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      if (!json?.success) throw new Error(json?.message || "Gagal menghapus submission.");

      if (openSubmission?.id === submissionId) setOpenSubmission(null);
      if (editSubmission?.id === submissionId) setEditSubmission(null);
      await fetchSurveyData(selectedSurveyId);
    } catch (e) {
      console.error(e);
      setSurveyError(e.message || "Gagal menghapus submission.");
    } finally {
      setDeletingSubmissionId(null);
    }
  }

  async function deleteOrder(row) {
    const id = Number(row?.id);
    if (!Number.isFinite(id) || deletingOrderId === id) return;

    const ok = window.confirm(`Hapus order #${id}? Tindakan ini tidak dapat dibatalkan.`);
    if (!ok) return;

    setDeletingOrderId(id);
    setOrdersError("");
    try {
      const res = await fetch(`${API}/raw-data/orders/${id}`, {
        method: "DELETE",
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      if (!json?.success) throw new Error(json?.message || "Gagal menghapus order.");
      await fetchOrders();
    } catch (e) {
      console.error(e);
      setOrdersError(e.message || "Gagal menghapus order.");
    } finally {
      setDeletingOrderId(null);
    }
  }

  async function deleteSelfAssessmentRow(row) {
    const id = Number(row?.id);
    if (!Number.isFinite(id) || deletingSelfAssessmentId === id) return;

    const ok = window.confirm(`Hapus data self assessment #${id}? Tindakan ini tidak dapat dibatalkan.`);
    if (!ok) return;

    const attemptId = Number(row?.attempt_id);
    const query = Number.isFinite(attemptId) && attemptId > 0
      ? `?attempt_id=${encodeURIComponent(attemptId)}`
      : "";

    setDeletingSelfAssessmentId(id);
    setSelfAssessmentError("");
    try {
      const res = await fetch(`${API}/raw-data/self-assessments/${id}${query}`, {
        method: "DELETE",
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      if (!json?.success) throw new Error(json?.message || "Gagal menghapus data self assessment.");
      if ((openSelfAssessment?.competencies || []).some((item) => Number(item.id) === id)) {
        setOpenSelfAssessment(null);
      }
      await fetchSelfAssessmentData();
    } catch (e) {
      console.error(e);
      setSelfAssessmentError(e.message || "Gagal menghapus data self assessment.");
    } finally {
      setDeletingSelfAssessmentId(null);
    }
  }

  function getSelfAssessmentGroupKey(row) {
    if (row?.attempt_id) return `attempt_${row.attempt_id}`;
    return [
      "legacy",
      row?.respondent_key || row?.guest_session_id || row?.user_id || "unknown",
      row?.role || "",
      row?.timeline || "",
      row?.respondent_type || "",
    ].join("__");
  }

  async function deleteSelfAssessmentGroup(group) {
    const key = group?.key;
    const rows = Array.isArray(group?.competencies) ? group.competencies : [];
    if (!key || !rows.length || deletingSelfAssessmentId === key) return;

    const ok = window.confirm(
      `Hapus self assessment ${group.respondent_name || "-"} (${rows.length} kompetensi)? Tindakan ini tidak dapat dibatalkan.`
    );
    if (!ok) return;

    setDeletingSelfAssessmentId(key);
    setSelfAssessmentError("");
    try {
      for (const row of rows) {
        const id = Number(row?.id);
        if (!Number.isFinite(id)) continue;
        const attemptId = Number(row?.attempt_id);
        const query = Number.isFinite(attemptId) && attemptId > 0
          ? `?attempt_id=${encodeURIComponent(attemptId)}`
          : "";
        const res = await fetch(`${API}/raw-data/self-assessments/${id}${query}`, {
          method: "DELETE",
          headers: authHeaders(user),
          credentials: "include",
        });
        const json = await asJsonOrThrow(res);
        if (!json?.success) throw new Error(json?.message || "Gagal menghapus data self assessment.");
      }
      if (openSelfAssessment?.key === key) setOpenSelfAssessment(null);
      await fetchSelfAssessmentData();
    } catch (e) {
      console.error(e);
      setSelfAssessmentError(e.message || "Gagal menghapus data self assessment.");
    } finally {
      setDeletingSelfAssessmentId(null);
    }
  }

  async function deleteAoRow(row) {
    const id = Number(row?.id);
    if (!Number.isFinite(id) || deletingAoId === id) return;

    const ok = window.confirm(`Hapus data AO #${id}? Tindakan ini tidak dapat dibatalkan.`);
    if (!ok) return;

    setDeletingAoId(id);
    setAoError("");
    try {
      const res = await fetch(`${API}/raw-data/ao/${id}`, {
        method: "DELETE",
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      if (!json?.success) throw new Error(json?.message || "Gagal menghapus data AO.");
      if (openAoRow?.id === id) setOpenAoRow(null);
      await fetchAoData();
    } catch (e) {
      console.error(e);
      setAoError(e.message || "Gagal menghapus data AO.");
    } finally {
      setDeletingAoId(null);
    }
  }

  function openEditAoRowModal(row) {
    setEditAoRow({
      id: row.id,
      user_id: row.user_id ?? "",
      farmer_name: row.farmer_name ?? "",
      desa: row.desa ?? "",
      kecamatan: row.kecamatan ?? "",
      kabupaten: row.kabupaten ?? "",
      provinsi: row.provinsi ?? "",
      answersJsonText: JSON.stringify(row.answers_json ?? {}, null, 2),
      recommendationsJsonText: JSON.stringify(row.recommendations_json ?? [], null, 2),
    });
    setEditAoError("");
  }

  function updateEditAoField(key, value) {
    setEditAoRow((prev) => ({ ...prev, [key]: value }));
  }

  async function saveEditedAoRow() {
    if (!editAoRow?.id || savingAoRow) return;

    let answersJson;
    let recommendationsJson;
    try {
      answersJson = JSON.parse(editAoRow.answersJsonText || "{}");
      recommendationsJson = JSON.parse(editAoRow.recommendationsJsonText || "[]");
    } catch {
      setEditAoError("Format JSON jawaban atau rekomendasi tidak valid.");
      return;
    }
    if (!answersJson || typeof answersJson !== "object" || Array.isArray(answersJson)) {
      setEditAoError("Answers JSON harus berupa object.");
      return;
    }
    if (!Array.isArray(recommendationsJson)) {
      setEditAoError("Recommendations JSON harus berupa array.");
      return;
    }

    const required = ["farmer_name", "desa", "kecamatan", "kabupaten", "provinsi"];
    if (required.some((key) => !String(editAoRow[key] || "").trim())) {
      setEditAoError("Nama, desa, kecamatan, kabupaten, dan provinsi wajib diisi.");
      return;
    }

    setSavingAoRow(true);
    setEditAoError("");
    try {
      const res = await fetch(`${API}/raw-data/ao/${editAoRow.id}`, {
        method: "PUT",
        headers: {
          ...authHeaders(user, { "Content-Type": "application/json" }),
        },
        credentials: "include",
        body: JSON.stringify({
          user_id: editAoRow.user_id === "" ? null : editAoRow.user_id,
          farmer_name: editAoRow.farmer_name,
          desa: editAoRow.desa,
          kecamatan: editAoRow.kecamatan,
          kabupaten: editAoRow.kabupaten,
          provinsi: editAoRow.provinsi,
          answers_json: answersJson,
          recommendations_json: recommendationsJson,
        }),
      });
      const json = await asJsonOrThrow(res);
      if (!json?.success) throw new Error(json?.message || "Gagal memperbarui data AO.");
      if (openAoRow?.id === editAoRow.id) setOpenAoRow(null);
      await fetchAoData();
      setEditAoRow(null);
    } catch (e) {
      console.error(e);
      setEditAoError(e.message || "Gagal memperbarui data AO.");
    } finally {
      setSavingAoRow(false);
    }
  }

  const AO_QUESTION_DEF = [
    { key: "q1", label: "1. Kondisi tanah kebun" },
    { key: "q2", label: "2. Kesehatan tanah" },
    { key: "q4_ph", label: "3. pH tanah" },
    { key: "q5", label: "4. Kesehatan tanaman" },
    { key: "q6", label: "5. Metode rehabilitasi" },
    { key: "q7_clones", label: "6. Jenis clone yang dikembangkan" },
    { key: "q8_cloneOther", label: "7. Jenis clone lainnya" },
    { key: "q9_totalTrees", label: "8. Total jumlah pohon" },
    { key: "q10_mcc02", label: "9. Jumlah clone MCC02" },
    { key: "q11_s2", label: "10. Jumlah clone S2" },
    { key: "q12_s1", label: "11. Jumlah clone S1" },
    { key: "q13_bb01", label: "12. Jumlah clone BB01" },
    { key: "q14_isc1scc11", label: "13. Jumlah clone ISC1/SCC11" },
    { key: "q15_other", label: "14. Jumlah clone lainnya" },
    { key: "q16_lokalhybrid", label: "15. Jumlah lokal/hybrid" },
    { key: "q18_age", label: "16. Usia tanaman (tahun)" },
    { key: "q19_suddenDeath", label: "17. Tanaman mati tiba-tiba" },
    { key: "q20_pruning", label: "18. Kualitas pemangkasan" },
    { key: "q21_pest", label: "19. Tingkat serangan hama/penyakit" },
    { key: "q22_weeds", label: "20. Kondisi rumput (gulma)" },
    { key: "q23_shade", label: "21. Kondisi pohon pelindung" },
    { key: "q24_fertilizerFormula", label: "22. Formulasi pupuk" },
    { key: "q25_fertilizerDose", label: "23. Dosis pupuk" },
    { key: "multiclone", label: "Auto. Multiclone" },
  ];

  function formatAoAnswerValue(value) {
    if (value == null || value === "") return "-";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
    if (typeof value === "object") {
      if (value?.status != null || value?.pct != null) {
        const pct = Number.isFinite(Number(value?.pct)) ? `${Number(value.pct).toFixed(1)}%` : "-";
        return `status: ${value?.status ?? "-"}, persentase: ${pct}`;
      }
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  async function fetchAoData(overrideFilters) {
    setAoLoading(true);
    setAoError("");
    setAoRows([]);
    try {
      const activeFilters = overrideFilters || aoFilters;
      const params = { limit: "5000" };
      Object.entries(activeFilters || {}).forEach(([k, v]) => {
        const val = String(v ?? "").trim();
        if (val) params[k] = val;
      });
      const q = new URLSearchParams(params);
      const res = await fetch(`${API}/raw-data/ao?${q.toString()}`, {
        headers: authHeaders(user),
        credentials: "include",
      });
      const json = await asJsonOrThrow(res);
      setAoRows(json.data || []);
    } catch (e) {
      console.error(e);
      setAoError(e.message || "Gagal memuat data AO.");
    } finally {
      setAoLoading(false);
    }
  }

  // ---- Excel Export (Survey)
  const surveyExportRows = useMemo(() => {
    // Flatten per submission: fixed cols + dynamic question columns
    if (!surveyRows?.length) return { rows: [], headers: [] };

    const questionDefsMap = new Map();
    for (const sub of surveyRows) {
      for (const a of sub.answers || []) {
        const key = getSurveyExportQuestionKey(a);
        if (!key || questionDefsMap.has(key)) continue;
        questionDefsMap.set(key, {
          key,
          header: getSurveyExportQuestionHeader(a),
          qOrder:
            a?.q_order != null && Number.isFinite(Number(a.q_order))
              ? Number(a.q_order)
              : Number.MAX_SAFE_INTEGER,
          questionText: String(a?.question_text || "").trim(),
        });
      }
    }
    const questionColumns = Array.from(questionDefsMap.values()).sort((a, b) => {
      if (a.qOrder !== b.qOrder) return a.qOrder - b.qOrder;
      return a.questionText.localeCompare(b.questionText, "id", { sensitivity: "base" });
    });

    const rows = surveyRows.map((sub) => {
      const o = {
        submission_id: sub.id,
        survey_id: sub.survey_id,
        survey_title: sub.survey_title || surveyMeta?.title || "",
        version: sub.version,
        respondent_user_id: sub.respondent_user_id ?? "",
        respondent_name: sub.respondent_name ?? "",
        respondent_phone: sub.respondent_phone ?? "",
        status: sub.status ?? "",
        submitted_at: sub.submitted_at ?? "",
        created_at: sub.created_at ?? "",
      };

      const mapAns = new Map();
      for (const a of sub.answers || []) {
        const key = getSurveyExportQuestionKey(a);
        if (!key) continue;
        mapAns.set(key, normalizeAnswer(a));
      }
      questionColumns.forEach((col) => {
        o[col.header] = mapAns.get(col.key) ?? "";
      });
      return o;
    });

    return {
      rows,
      headers: [
        "submission_id",
        "survey_id",
        "survey_title",
        "version",
        "respondent_user_id",
        "respondent_name",
        "respondent_phone",
        "status",
        "submitted_at",
        "created_at",
        ...questionColumns.map((col) => col.header),
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyRows, surveyMeta]);

  function exportSurveyToExcel() {
    if (!surveyExportRows.rows.length) return;
    const ws = XLSX.utils.json_to_sheet(surveyExportRows.rows, { header: surveyExportRows.headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Survey Raw Data");

    const safeTitle = (surveyMeta?.title || "Survey")
      .replace(/[\\/:*?"<>|]/g, "-")
      .slice(0, 80);

    XLSX.writeFile(wb, `RawData_${safeTitle}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  // ---- Excel Export (Orders)
  function exportOrdersToExcel() {
    if (!orders?.length) return;
    const ws = XLSX.utils.json_to_sheet(orders);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders Raw Data");
    XLSX.writeFile(wb, `RawData_Orders_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  function exportSelfAssessmentToExcel() {
    if (!selfAssessmentRows?.length) return;
    const rows = selfAssessmentRows.map((r) => ({
      id: r.id,
      attempt_id: r.attempt_id ?? "",
      user_id: r.user_id ?? "",
      guest_session_id: r.guest_session_id ?? "",
      respondent_key: r.respondent_key ?? "",
      respondent_type: r.respondent_type ?? "",
      respondent_name: r.respondent_name ?? "",
      respondent_institution: r.respondent_institution ?? "",
      respondent_address: r.respondent_address ?? "",
      role: r.role ?? "",
      timeline: r.timeline ?? "",
      competency_type: r.competency_type ?? "",
      capability_title: r.capability_title ?? "",
      competency_id: r.competency_id ?? "",
      competency_name: r.competency_name ?? "",
      pathway_standard: r.pathway_standard ?? "",
      self_grade: r.self_grade ?? "",
      notes: r.notes ?? "",
      created_at: r.created_at ?? "",
      updated_at: r.updated_at ?? "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Self Assessment");
    XLSX.writeFile(wb, `RawData_SelfAssessment_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  const selfAssessmentGroups = useMemo(() => {
    const map = new Map();
    for (const row of selfAssessmentRows || []) {
      const key = getSelfAssessmentGroupKey(row);
      if (!map.has(key)) {
        map.set(key, {
          key,
          attempt_id: row.attempt_id ?? null,
          user_id: row.user_id ?? null,
          guest_session_id: row.guest_session_id ?? null,
          respondent_key: row.respondent_key ?? "",
          respondent_type: row.respondent_type ?? "",
          respondent_name: row.respondent_name ?? "",
          respondent_institution: row.respondent_institution ?? "",
          respondent_address: row.respondent_address ?? "",
          role: row.role ?? "",
          timeline: row.timeline ?? "",
          created_at: row.created_at ?? "",
          updated_at: row.updated_at ?? row.created_at ?? "",
          competencies: [],
        });
      }

      const group = map.get(key);
      group.competencies.push(row);

      const currentTime = new Date(group.updated_at || group.created_at || 0).getTime();
      const rowTime = new Date(row.updated_at || row.created_at || 0).getTime();
      if (Number.isFinite(rowTime) && (!Number.isFinite(currentTime) || rowTime > currentTime)) {
        group.updated_at = row.updated_at || row.created_at || group.updated_at;
      }
    }

    return Array.from(map.values())
      .map((group) => ({
        ...group,
        competency_count: group.competencies.length,
        competency_types: Array.from(
          new Set(group.competencies.map((r) => r.competency_type).filter(Boolean))
        ).join(", "),
        competencies: group.competencies.slice().sort((a, b) => {
          const typeCmp = String(a.competency_type || "").localeCompare(String(b.competency_type || ""), "id");
          if (typeCmp) return typeCmp;
          return String(a.competency_name || "").localeCompare(String(b.competency_name || ""), "id");
        }),
      }))
      .sort((a, b) => {
        const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
        const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
        return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfAssessmentRows]);

  function exportAoToExcel() {
    if (!aoRows?.length) return;
    const rows = aoRows.map((r) => ({
      id: r.id,
      user_id: r.user_id ?? "",
      farmer_name: r.farmer_name ?? "",
      desa: r.desa ?? "",
      kecamatan: r.kecamatan ?? "",
      kabupaten: r.kabupaten ?? "",
      provinsi: r.provinsi ?? "",
      recommendation_count: r.recommendation_count ?? 0,
      recommendation_titles: r.recommendation_titles ?? "",
      created_at: r.created_at ?? "",
      answers_json: typeof r.answers_json === "string" ? r.answers_json : JSON.stringify(r.answers_json ?? {}),
      recommendations_json:
        typeof r.recommendations_json === "string"
          ? r.recommendations_json
          : JSON.stringify(r.recommendations_json ?? []),
      ...Object.fromEntries(
        AO_QUESTION_DEF.map((q) => [
          q.label,
          formatAoAnswerValue((r.answers_json || {})[q.key]),
        ])
      ),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AO Raw Data");
    XLSX.writeFile(wb, `RawData_AO_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  function exportNewsAnalyticsToExcel() {
    if (!newsAnalyticsRows.length) return;
    const summarySheet = XLSX.utils.json_to_sheet([
      {
        "Halaman Dibuka": newsAnalyticsSummary.page_opens,
        "Pengunjung Unik": newsAnalyticsSummary.unique_visitors,
        "Sesi Unik": newsAnalyticsSummary.unique_sessions,
      },
    ]);
    const detailRows = newsAnalyticsRows.map((row) => ({
      "ID Berita": row.news_id,
      Judul: row.title,
      "Tanggal Berita": row.created_at,
      "Dilihat": Number(row.news_views || 0),
      "Pengunjung Unik": Number(row.unique_viewers || 0),
      "Klik Lihat Selengkapnya": Number(row.expand_clicks || 0),
      "Klik PDF": Number(row.pdf_clicks || 0),
      "Klik Video": Number(row.video_clicks || 0),
      "Klik Foto": Number(row.photo_clicks || 0),
    }));
    const detailSheet = XLSX.utils.json_to_sheet(detailRows);
    const mediaAccessSheet = XLSX.utils.json_to_sheet(
      newsMediaAccessRows.map((row) => ({
        Nama: row.visitor_name,
        Institusi: row.institution,
        "Judul Berita": row.title,
        "Jenis Akses":
          row.event_type === "pdf_click"
            ? "PDF"
            : row.event_type === "video_click"
              ? "Video"
              : "Foto",
        "Waktu Akses": row.accessed_at,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Ringkasan Halaman");
    XLSX.utils.book_append_sheet(workbook, detailSheet, "Per Berita");
    XLSX.utils.book_append_sheet(workbook, mediaAccessSheet, "Pengakses Media");
    XLSX.writeFile(workbook, `RawData_Berita_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  return (
    <div className="rawdata">
      <div className="rd-head">
        <div className="rd-head-main">
          <h1>Raw Data</h1>
          <div className="rd-tabs">
            <button
              className={`rd-tab ${tab === "survey" ? "active" : ""}`}
              onClick={() => setTab("survey")}
            >
              Survey Data
            </button>
            <button
              className={`rd-tab ${tab === "orders" ? "active" : ""}`}
              onClick={() => setTab("orders")}
            >
              Order Data
            </button>
            <button
              className={`rd-tab ${tab === "selfAssessment" ? "active" : ""}`}
              onClick={() => setTab("selfAssessment")}
            >
              Self Assessment
            </button>
            <button
              className={`rd-tab ${tab === "ao" ? "active" : ""}`}
              onClick={() => setTab("ao")}
            >
              Data AO
            </button>
            <button
              className={`rd-tab ${tab === "news" ? "active" : ""}`}
              onClick={() => setTab("news")}
            >
              Berita
            </button>
          </div>
        </div>

        <div className="rd-head-actions">
          {tab === "survey" ? (
            <button
              className="rd-btn primary"
              onClick={exportSurveyToExcel}
              disabled={!surveyRows.length}
              title={!surveyRows.length ? "Pilih survey & pastikan ada data" : "Export ke Excel"}
            >
              Export Excel
            </button>
          ) : tab === "orders" ? (
            <button
              className="rd-btn primary"
              onClick={exportOrdersToExcel}
              disabled={!orders.length}
            >
              Export Excel
            </button>
          ) : tab === "selfAssessment" ? (
            <button
              className="rd-btn primary"
              onClick={exportSelfAssessmentToExcel}
              disabled={!selfAssessmentRows.length}
            >
              Export Excel
            </button>
          ) : tab === "news" ? (
            <button
              className="rd-btn primary"
              onClick={exportNewsAnalyticsToExcel}
              disabled={!newsAnalyticsRows.length}
            >
              Export Excel
            </button>
          ) : (
            <button
              className="rd-btn primary"
              onClick={exportAoToExcel}
              disabled={!aoRows.length}
            >
              Export Excel
            </button>
          )}
        </div>
      </div>

      {/* ===== Survey Tab ===== */}
      {tab === "survey" && (
        <>
          <div className="rd-toolbar">
            <div className="rd-field">
              <label>Survey Title</label>
              <select
                value={selectedSurveyId}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedSurveyId(id);
                  if (id) fetchSurveyData(id);
                }}
              >
                <option value="">-- Pilih Survey --</option>
                {surveyTitles.map((s) => (
                  <option key={s.survey_id} value={s.survey_id}>
                    {s.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="rd-toolbar-right">
              <button
                className="rd-btn"
                onClick={() => selectedSurveyId && fetchSurveyData(selectedSurveyId)}
                disabled={!selectedSurveyId || surveyLoading}
              >
                Refresh
              </button>
            </div>
          </div>

          {surveyError ? <div className="rd-alert error">{surveyError}</div> : null}

          {!selectedSurveyId ? (
            <div className="rd-empty">
              Pilih survey untuk menampilkan raw data.
            </div>
          ) : surveyLoading ? (
            <div className="rd-loading">Memuat data survey...</div>
          ) : !surveyRows.length ? (
            <div className="rd-empty">Belum ada submission untuk survey ini.</div>
          ) : (
            <div className="rd-card">
              <div className="rd-card-head">
                <div className="rd-meta">
                  Total submissions: <b>{surveyRows.length}</b>
                </div>
              </div>

              <div className="rd-table-wrap">
                <table className="rd-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Version</th>
                      <th>Respondent</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th className="right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {surveyRows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.id}</td>
                        <td>v{r.version}</td>
                        <td>{r.respondent_name || "-"}</td>
                        <td>{r.respondent_phone || "-"}</td>
                        <td>
                          <span className={`rd-badge ${String(r.status || "").toLowerCase()}`}>
                            {r.status || "-"}
                          </span>
                        </td>
                        <td>{formatDt(r.submitted_at || r.created_at)}</td>
                        <td className="right">
                          <button className="rd-btn small" onClick={() => setOpenSubmission(r)}>
                            View
                          </button>
                          <button className="rd-btn small rd-btn-edit" onClick={() => openEditSubmissionModal(r)}>
                            Edit
                          </button>
                          <button
                            className="rd-btn small rd-btn-delete"
                            onClick={() => deleteSubmission(r)}
                            disabled={deletingSubmissionId === r.id}
                          >
                            {deletingSubmissionId === r.id ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Modal detail submission */}
          {openSubmission && (
            <div className="rd-modal" onClick={() => setOpenSubmission(null)}>
              <div className="rd-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="rd-modal-head">
                  <div>
                    <div className="rd-modal-title">
                      Detail Submission #{openSubmission.id} - v{openSubmission.version}
                    </div>
                    <div className="rd-modal-sub">
                      {openSubmission.respondent_name || "-"} - {openSubmission.respondent_phone || "-"} -{" "}
                      {formatDt(openSubmission.submitted_at || openSubmission.created_at)}
                    </div>
                  </div>
                  <button className="rd-close" onClick={() => setOpenSubmission(null)}>
                    x
                  </button>
                </div>

                <div className="rd-modal-body">
                  {(openSubmission.answers || []).length === 0 ? (
                    <div className="rd-empty">Tidak ada jawaban.</div>
                  ) : (
                    (openSubmission.answers || []).map((a, idx) => (
                      <div className="rd-qa" key={idx}>
                        <div className="q">
                          {(a.q_order != null ? `${a.q_order}. ` : "")}
                          {a.question_text || `Q${a.q_order}`}
                        </div>
                        <div className="a">
                          {getMediaUrl(a) ? (
                            <a
                              className="rd-media-link"
                              href={getMediaUrl(a)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {getMediaLabel(a)}
                            </a>
                          ) : isGpsAnswer(a) ? (
                            <span className="rd-gps-value">{formatGpsAnswer(a) || "-"}</span>
                          ) : (
                            String(normalizeAnswer(a) ?? "") || "-"
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="rd-modal-foot">
                  <button className="rd-btn" onClick={() => setOpenSubmission(null)}>
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== Orders Tab ===== */}
      {tab === "orders" && (
        <>
          {ordersError ? <div className={`rd-alert ${orders.length ? "info" : "error"}`}>{ordersError}</div> : null}

          {ordersLoading ? (
            <div className="rd-loading">Memuat data order...</div>
          ) : !orders.length ? (
            <div className="rd-empty">Belum ada data order (atau query orders belum disesuaikan).</div>
          ) : (
            <div className="rd-card">
              <div className="rd-card-head">
                <div>
                  <div className="rd-title">Order Data</div>
                  <div className="rd-meta">
                    Total orders: <b>{orders.length}</b>
                  </div>
                </div>
              </div>

              <div className="rd-table-wrap">
                <table className="rd-table">
                  <thead>
                    <tr>
                      {Object.keys(orders[0] || {}).map((k) => (
                        <th key={k}>{k}</th>
                      ))}
                      <th className="right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o, idx) => (
                      <tr key={o.id ?? idx}>
                        {Object.keys(orders[0] || {}).map((k) => (
                          <td key={k}>{o?.[k] ?? ""}</td>
                        ))}
                        <td className="right">
                          <button
                            className="rd-btn small rd-btn-delete"
                            onClick={() => deleteOrder(o)}
                            disabled={deletingOrderId === Number(o.id)}
                          >
                            {deletingOrderId === Number(o.id) ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="rd-hint">
                * Jika kolom order Anda lebih banyak/berbeda, export Excel tetap memuat semua kolom (full JSON).
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== Self Assessment Tab ===== */}
      {tab === "selfAssessment" && (
        <>
          <div className="rd-toolbar rd-toolbar-self">
            <div className="rd-field">
              <label>Role</label>
              <select
                value={selfAssessmentFilters.role}
                onChange={(e) => setSelfAssessmentFilters((p) => ({ ...p, role: e.target.value }))}
              >
                <option value="">Semua Role</option>
                <option value="Keluarga Petani">Keluarga Petani</option>
                <option value="Penggerak Pemuda">Penggerak Pemuda</option>
                <option value="Agripreneur">Agripreneur</option>
                <option value="Fasilitator Lapangan">Fasilitator Lapangan</option>
                <option value="Supervisor">Supervisor</option>
              </select>
            </div>

            <div className="rd-field">
              <label>Tahapan</label>
              <select
                value={selfAssessmentFilters.timeline}
                onChange={(e) => setSelfAssessmentFilters((p) => ({ ...p, timeline: e.target.value }))}
              >
                <option value="">Semua Tahapan</option>
                <option value="3 Tahun Pertama">3 Tahun Pertama</option>
                <option value="3 Tahun Kedua">3 Tahun Kedua</option>
                <option value="3 Tahun Ketiga">3 Tahun Ketiga</option>
                <option value="Opsional">Opsional</option>
              </select>
            </div>

            <div className="rd-field">
              <label>Jenis</label>
              <select
                value={selfAssessmentFilters.type}
                onChange={(e) => setSelfAssessmentFilters((p) => ({ ...p, type: e.target.value }))}
              >
                <option value="">Semua Jenis</option>
                <option value="Fungsional">Fungsional</option>
                <option value="Kepemimpinan">Kepemimpinan</option>
              </select>
            </div>

            <div className="rd-field">
              <label>Responden</label>
              <select
                value={selfAssessmentFilters.respondent_type}
                onChange={(e) => setSelfAssessmentFilters((p) => ({ ...p, respondent_type: e.target.value }))}
              >
                <option value="">Semua</option>
                <option value="user">Login</option>
                <option value="guest">Sebelum Login</option>
              </select>
            </div>

            <div className="rd-field">
              <label>Cari</label>
              <input
                value={selfAssessmentFilters.q}
                onChange={(e) => setSelfAssessmentFilters((p) => ({ ...p, q: e.target.value }))}
                placeholder="Nama, institusi, kompetensi"
              />
            </div>

            <div className="rd-toolbar-right">
              <button className="rd-btn" onClick={fetchSelfAssessmentData} disabled={selfAssessmentLoading}>
                {selfAssessmentLoading ? "Memuat..." : "Filter"}
              </button>
              <button
                className="rd-btn"
                onClick={() => {
                  const empty = {
                    role: "",
                    timeline: "",
                    type: "",
                    respondent_type: "",
                    q: "",
                  };
                  setSelfAssessmentFilters(empty);
                  fetchSelfAssessmentData(empty);
                }}
              >
                Reset
              </button>
            </div>
          </div>

          {selfAssessmentError ? <div className="rd-alert error">{selfAssessmentError}</div> : null}

          {selfAssessmentLoading ? (
            <div className="rd-loading">Memuat data self assessment...</div>
          ) : !selfAssessmentGroups.length ? (
            <div className="rd-empty">Belum ada data self assessment yang cocok dengan filter.</div>
          ) : (
            <div className="rd-card">
              <div className="rd-card-head">
                <div className="rd-meta">
                  Total assessment: <b>{selfAssessmentGroups.length}</b> | Total kompetensi untuk export:{" "}
                  <b>{selfAssessmentRows.length}</b>
                </div>
              </div>

              <div className="rd-table-wrap">
                <table className="rd-table">
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>Attempt ID</th>
                      <th>Tanggal</th>
                      <th>Akses</th>
                      <th>Nama</th>
                      <th>Institusi</th>
                      <th>Alamat</th>
                      <th>Role</th>
                      <th>Tahapan</th>
                      <th>Jenis</th>
                      <th>Jumlah Kompetensi</th>
                      <th className="right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selfAssessmentGroups.map((r, index) => (
                      <tr key={r.key}>
                        <td>{index + 1}</td>
                        <td>{r.attempt_id || "-"}</td>
                        <td>{formatDt(r.updated_at || r.created_at)}</td>
                        <td>{r.respondent_type === "guest" ? "Sebelum Login" : "Login"}</td>
                        <td>{r.respondent_name || "-"}</td>
                        <td>{r.respondent_institution || "-"}</td>
                        <td>{r.respondent_address || "-"}</td>
                        <td>{r.role || "-"}</td>
                        <td>{r.timeline || "-"}</td>
                        <td>{r.competency_types || "-"}</td>
                        <td>{r.competency_count}</td>
                        <td className="right">
                          <button className="rd-btn small" onClick={() => setOpenSelfAssessment(r)}>
                            View
                          </button>
                          <button
                            className="rd-btn small rd-btn-delete"
                            onClick={() => deleteSelfAssessmentGroup(r)}
                            disabled={deletingSelfAssessmentId === r.key}
                          >
                            {deletingSelfAssessmentId === r.key ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {openSelfAssessment && (
            <div className="rd-modal" onClick={() => setOpenSelfAssessment(null)}>
              <div className="rd-modal-content rd-modal-content-wide" onClick={(e) => e.stopPropagation()}>
                <div className="rd-modal-head">
                  <div>
                    <div className="rd-modal-title">
                      Detail Self Assessment - {openSelfAssessment.respondent_name || "-"}
                    </div>
                    <div className="rd-modal-sub">
                      {openSelfAssessment.respondent_institution || "-"} | {openSelfAssessment.role || "-"} |{" "}
                      {openSelfAssessment.timeline || "-"} | {formatDt(openSelfAssessment.updated_at || openSelfAssessment.created_at)}
                    </div>
                  </div>
                  <button className="rd-close" onClick={() => setOpenSelfAssessment(null)}>
                    x
                  </button>
                </div>

                <div className="rd-modal-body">
                  <div className="rd-compact-summary">
                    <div><b>Akses:</b> {openSelfAssessment.respondent_type === "guest" ? "Sebelum Login" : "Login"}</div>
                    <div><b>Alamat:</b> {openSelfAssessment.respondent_address || "-"}</div>
                    <div><b>Total kompetensi:</b> {openSelfAssessment.competency_count}</div>
                  </div>

                  <div className="rd-table-wrap rd-table-wrap-modal">
                    <table className="rd-table rd-table-wide">
                      <thead>
                        <tr>
                          <th>No.</th>
                          <th>Jenis</th>
                          <th>Capability</th>
                          <th>Kompetensi</th>
                          <th>Standar</th>
                          <th>Nilai Diri</th>
                          <th>Catatan</th>
                          <th className="right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(openSelfAssessment.competencies || []).map((item, idx) => (
                          <tr key={item.id || idx}>
                            <td>{idx + 1}</td>
                            <td>{item.competency_type || "-"}</td>
                            <td>{item.capability_title || "-"}</td>
                            <td>{item.competency_name || "-"}</td>
                            <td>{item.pathway_standard || "-"}</td>
                            <td>{item.self_grade || "-"}</td>
                            <td>{item.notes || "-"}</td>
                            <td className="right">
                              <button
                                className="rd-btn small rd-btn-delete"
                                onClick={() => deleteSelfAssessmentRow(item)}
                                disabled={deletingSelfAssessmentId === Number(item.id)}
                              >
                                {deletingSelfAssessmentId === Number(item.id) ? "Deleting..." : "Delete"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="rd-modal-foot">
                  <button className="rd-btn" onClick={() => setOpenSelfAssessment(null)}>
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== News Analytics Tab ===== */}
      {tab === "news" && (
        <>
          <div className="rd-toolbar rd-toolbar-news">
            <div>
              <div className="rd-title">Analytics Berita Kakao</div>
              <div className="rd-meta">Data interaksi pengunjung pada halaman dan masing-masing berita.</div>
            </div>
            <div className="rd-toolbar-right">
              <button className="rd-btn" onClick={fetchNewsAnalytics} disabled={newsAnalyticsLoading}>
                {newsAnalyticsLoading ? "Memuat..." : "Refresh"}
              </button>
            </div>
          </div>

          {newsAnalyticsError ? <div className="rd-alert error">{newsAnalyticsError}</div> : null}

          <div className="rd-news-summary">
            <div className="rd-news-stat">
              <span>Halaman Dibuka</span>
              <strong>{newsAnalyticsSummary.page_opens.toLocaleString("id-ID")}</strong>
            </div>
            <div className="rd-news-stat">
              <span>Pengunjung Unik</span>
              <strong>{newsAnalyticsSummary.unique_visitors.toLocaleString("id-ID")}</strong>
            </div>
            <div className="rd-news-stat">
              <span>Sesi Kunjungan</span>
              <strong>{newsAnalyticsSummary.unique_sessions.toLocaleString("id-ID")}</strong>
            </div>
          </div>

          {newsAnalyticsLoading ? (
            <div className="rd-loading">Memuat analytics berita...</div>
          ) : !newsAnalyticsRows.length ? (
            <div className="rd-empty">Belum ada berita untuk ditampilkan.</div>
          ) : (
            <div className="rd-card">
              <div className="rd-card-head">
                <div className="rd-meta">
                  Total berita: <b>{newsAnalyticsRows.length}</b>
                </div>
              </div>
              <div className="rd-table-wrap">
                <table className="rd-table rd-news-table">
                  <thead>
                    <tr>
                      <th>Judul Berita</th>
                      <th>Dilihat</th>
                      <th>Pengunjung Unik</th>
                      <th>Lihat Selengkapnya</th>
                      <th>PDF</th>
                      <th>Video</th>
                      <th>Foto</th>
                      <th>Tanggal Berita</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newsAnalyticsRows.map((row) => (
                      <tr key={row.news_id}>
                        <td className="rd-news-title-cell">{row.title || "-"}</td>
                        <td>{Number(row.news_views || 0).toLocaleString("id-ID")}</td>
                        <td>{Number(row.unique_viewers || 0).toLocaleString("id-ID")}</td>
                        <td>{Number(row.expand_clicks || 0).toLocaleString("id-ID")}</td>
                        <td>{Number(row.pdf_clicks || 0).toLocaleString("id-ID")}</td>
                        <td>{Number(row.video_clicks || 0).toLocaleString("id-ID")}</td>
                        <td>{Number(row.photo_clicks || 0).toLocaleString("id-ID")}</td>
                        <td>{formatDt(row.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!newsAnalyticsLoading && newsMediaAccessRows.length > 0 && (
            <div className="rd-card rd-news-access-card">
              <div className="rd-card-head">
                <div className="rd-title">Detail Pengakses Media</div>
                <div className="rd-meta">
                  Nama dan institusi pengunjung yang membuka PDF, video, atau foto.
                </div>
              </div>
              <div className="rd-table-wrap">
                <table className="rd-table rd-news-access-table">
                  <thead>
                    <tr>
                      <th>Nama</th>
                      <th>Institusi</th>
                      <th>Judul Berita</th>
                      <th>Media</th>
                      <th>Waktu Akses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newsMediaAccessRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.visitor_name || "-"}</td>
                        <td>{row.institution || "-"}</td>
                        <td className="rd-news-title-cell">{row.title || "-"}</td>
                        <td>
                          <span className={`rd-news-media-badge ${row.event_type}`}>
                            {row.event_type === "pdf_click"
                              ? "PDF"
                              : row.event_type === "video_click"
                                ? "Video"
                                : "Foto"}
                          </span>
                        </td>
                        <td>{formatDt(row.accessed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== AO Tab ===== */}
      {tab === "ao" && (
        <>
          <div className="rd-toolbar rd-toolbar-ao">
            <div className="rd-field">
              <label>Desa</label>
              <input
                value={aoFilters.desa}
                onChange={(e) => setAoFilters((p) => ({ ...p, desa: e.target.value }))}
                placeholder="Filter desa"
              />
            </div>

            <div className="rd-field">
              <label>Kecamatan</label>
              <input
                value={aoFilters.kecamatan}
                onChange={(e) => setAoFilters((p) => ({ ...p, kecamatan: e.target.value }))}
                placeholder="Filter kecamatan"
              />
            </div>

            <div className="rd-field">
              <label>Kabupaten</label>
              <input
                value={aoFilters.kabupaten}
                onChange={(e) => setAoFilters((p) => ({ ...p, kabupaten: e.target.value }))}
                placeholder="Filter kabupaten"
              />
            </div>

            <div className="rd-field">
              <label>Provinsi</label>
              <input
                value={aoFilters.provinsi}
                onChange={(e) => setAoFilters((p) => ({ ...p, provinsi: e.target.value }))}
                placeholder="Filter provinsi"
              />
            </div>

            <div className="rd-toolbar-right">
              <button className="rd-btn" onClick={fetchAoData} disabled={aoLoading}>
                {aoLoading ? "Memuat..." : "Filter"}
              </button>
              <button
                className="rd-btn"
                onClick={() => {
                  setAoFilters({
                    desa: "",
                    kecamatan: "",
                    kabupaten: "",
                    provinsi: "",
                  });
                  fetchAoData({
                    desa: "",
                    kecamatan: "",
                    kabupaten: "",
                    provinsi: "",
                  });
                }}
              >
                Reset
              </button>
            </div>
          </div>

          {aoError ? <div className="rd-alert error">{aoError}</div> : null}

          {aoLoading ? (
            <div className="rd-loading">Memuat data AO...</div>
          ) : !aoRows.length ? (
            <div className="rd-empty">Belum ada data AO yang cocok dengan filter.</div>
          ) : (
            <div className="rd-card">
              <div className="rd-card-head">
                <div className="rd-meta">
                  Total data: <b>{aoRows.length}</b>
                </div>
              </div>

              <div className="rd-table-wrap">
                <table className="rd-table">
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>Nama</th>
                      <th>Desa</th>
                      <th>Kecamatan</th>
                      <th>Kabupaten</th>
                      <th>Provinsi</th>
                      <th>Jml Rekomendasi</th>
                      <th>Rekomendasi</th>
                      <th className="right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aoRows.map((r, index) => (
                      <tr key={r.id}>
                        <td>{index + 1}</td>
                        <td>{r.farmer_name || "-"}</td>
                        <td>{r.desa || "-"}</td>
                        <td>{r.kecamatan || "-"}</td>
                        <td>{r.kabupaten || "-"}</td>
                        <td>{r.provinsi || "-"}</td>
                        <td>{r.recommendation_count ?? 0}</td>
                        <td>{r.recommendation_titles || "-"}</td>
                        <td className="right">
                          <button className="rd-btn small" onClick={() => setOpenAoRow(r)}>
                            View
                          </button>
                          <button className="rd-btn small rd-btn-edit" onClick={() => openEditAoRowModal(r)}>
                            Edit
                          </button>
                          <button
                            className="rd-btn small rd-btn-delete"
                            onClick={() => deleteAoRow(r)}
                            disabled={deletingAoId === Number(r.id)}
                          >
                            {deletingAoId === Number(r.id) ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {openAoRow && (
            <div className="rd-modal" onClick={() => setOpenAoRow(null)}>
              <div className="rd-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="rd-modal-head">
                  <div>
                    <div className="rd-modal-title">
                      Detail Evaluasi AO #{openAoRow.id}
                    </div>
                    <div className="rd-modal-sub">
                      {openAoRow.farmer_name || "-"} - {openAoRow.desa || "-"}, {openAoRow.kecamatan || "-"},{" "}
                      {openAoRow.kabupaten || "-"}, {openAoRow.provinsi || "-"} - {formatDt(openAoRow.created_at)}
                    </div>
                  </div>
                  <button className="rd-close" onClick={() => setOpenAoRow(null)}>
                    x
                  </button>
                </div>

                <div className="rd-modal-body">
                  {AO_QUESTION_DEF.map((q) => (
                    <div className="rd-qa" key={q.key}>
                      <div className="q">{q.label}</div>
                      <div className="a">
                        {formatAoAnswerValue((openAoRow.answers_json || {})[q.key])}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="rd-modal-foot">
                  <button className="rd-btn" onClick={() => setOpenAoRow(null)}>
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          )}

          {editAoRow && (
            <div className="rd-modal" onClick={() => !savingAoRow && setEditAoRow(null)}>
              <div className="rd-modal-content rd-modal-content-wide" onClick={(e) => e.stopPropagation()}>
                <div className="rd-modal-head">
                  <div>
                    <div className="rd-modal-title">Edit Evaluasi AO #{editAoRow.id}</div>
                    <div className="rd-modal-sub">
                      Perbarui data profil evaluasi, jawaban, dan rekomendasi AO.
                    </div>
                  </div>
                  <button className="rd-close" onClick={() => !savingAoRow && setEditAoRow(null)}>
                    x
                  </button>
                </div>

                <div className="rd-modal-body">
                  {editAoError ? <div className="rd-alert error">{editAoError}</div> : null}

                  <div className="rd-edit-grid">
                    <label>
                      <span>User ID</span>
                      <input
                        className="rd-edit-input"
                        value={editAoRow.user_id}
                        onChange={(e) => updateEditAoField("user_id", e.target.value)}
                        placeholder="Kosongkan jika tidak ada"
                      />
                    </label>
                    <label>
                      <span>Nama</span>
                      <input
                        className="rd-edit-input"
                        value={editAoRow.farmer_name}
                        onChange={(e) => updateEditAoField("farmer_name", e.target.value)}
                      />
                    </label>
                    <label>
                      <span>Desa</span>
                      <input
                        className="rd-edit-input"
                        value={editAoRow.desa}
                        onChange={(e) => updateEditAoField("desa", e.target.value)}
                      />
                    </label>
                    <label>
                      <span>Kecamatan</span>
                      <input
                        className="rd-edit-input"
                        value={editAoRow.kecamatan}
                        onChange={(e) => updateEditAoField("kecamatan", e.target.value)}
                      />
                    </label>
                    <label>
                      <span>Kabupaten</span>
                      <input
                        className="rd-edit-input"
                        value={editAoRow.kabupaten}
                        onChange={(e) => updateEditAoField("kabupaten", e.target.value)}
                      />
                    </label>
                    <label>
                      <span>Provinsi</span>
                      <input
                        className="rd-edit-input"
                        value={editAoRow.provinsi}
                        onChange={(e) => updateEditAoField("provinsi", e.target.value)}
                      />
                    </label>
                  </div>

                  <div className="rd-qa">
                    <div className="q">Answers JSON</div>
                    <textarea
                      rows={12}
                      className="rd-edit-textarea rd-json-textarea"
                      value={editAoRow.answersJsonText}
                      onChange={(e) => updateEditAoField("answersJsonText", e.target.value)}
                    />
                  </div>

                  <div className="rd-qa">
                    <div className="q">Recommendations JSON</div>
                    <textarea
                      rows={8}
                      className="rd-edit-textarea rd-json-textarea"
                      value={editAoRow.recommendationsJsonText}
                      onChange={(e) => updateEditAoField("recommendationsJsonText", e.target.value)}
                    />
                  </div>
                </div>

                <div className="rd-modal-foot">
                  <button className="rd-btn" onClick={() => !savingAoRow && setEditAoRow(null)}>
                    Batal
                  </button>
                  <button className="rd-btn primary" onClick={saveEditedAoRow} disabled={savingAoRow}>
                    {savingAoRow ? "Menyimpan..." : "Simpan"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal edit submission */}
          {editSubmission && (
            <div className="rd-modal" onClick={() => !savingEditSubmission && setEditSubmission(null)}>
              <div className="rd-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="rd-modal-head">
                  <div>
                    <div className="rd-modal-title">
                      Edit Submission #{editSubmission.id} - v{editSubmission.version}
                    </div>
                    <div className="rd-modal-sub">
                      {editSubmission.respondent_name || "-"} - {editSubmission.respondent_phone || "-"} -{" "}
                      {formatDt(editSubmission.submitted_at || editSubmission.created_at)}
                    </div>
                  </div>
                  <button className="rd-close" onClick={() => !savingEditSubmission && setEditSubmission(null)}>
                    x
                  </button>
                </div>

                <div className="rd-modal-body">
                  {editSubmissionError ? <div className="rd-alert error">{editSubmissionError}</div> : null}
                  {(editSubmission.answers || []).length === 0 ? (
                    <div className="rd-empty">Tidak ada jawaban.</div>
                  ) : (
                    (editSubmission.answers || []).map((a, idx) => (
                      <div className="rd-qa" key={a.id || idx}>
                        <div className="q">
                          {getDisplayQuestionNo(a) != null ? `${getDisplayQuestionNo(a)}. ` : ""}
                          {a.question_text || `Q${a.q_order}`}
                        </div>
                        <div className="a">
                          {a.answerType === "number" ? (
                            <input
                              type="number"
                              className="rd-edit-input"
                              value={a.editValue}
                              onChange={(e) => {
                                const v = e.target.value;
                                setEditSubmission((prev) => ({
                                  ...prev,
                                  answers: prev.answers.map((x) =>
                                    x.id === a.id ? { ...x, editValue: v } : x
                                  ),
                                }));
                              }}
                            />
                          ) : (
                            <textarea
                              rows={a.answerType === "json" ? 6 : 3}
                              className="rd-edit-textarea"
                              value={a.editValue}
                              onChange={(e) => {
                                const v = e.target.value;
                                setEditSubmission((prev) => ({
                                  ...prev,
                                  answers: prev.answers.map((x) =>
                                    x.id === a.id ? { ...x, editValue: v } : x
                                  ),
                                }));
                              }}
                            />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="rd-modal-foot">
                  <button className="rd-btn" onClick={() => !savingEditSubmission && setEditSubmission(null)}>
                    Batal
                  </button>
                  <button className="rd-btn primary" onClick={saveEditedSubmission} disabled={savingEditSubmission}>
                    {savingEditSubmission ? "Menyimpan..." : "Simpan"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {editSubmission && (
        <div className="rd-modal" onClick={() => !savingEditSubmission && setEditSubmission(null)}>
          <div className="rd-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="rd-modal-head">
              <div>
                <div className="rd-modal-title">
                  Edit Submission #{editSubmission.id} - v{editSubmission.version}
                </div>
                <div className="rd-modal-sub">
                  {editSubmission.respondent_name || "-"} - {editSubmission.respondent_phone || "-"} -{" "}
                  {formatDt(editSubmission.submitted_at || editSubmission.created_at)}
                </div>
              </div>
              <button className="rd-close" onClick={() => !savingEditSubmission && setEditSubmission(null)}>
                x
              </button>
            </div>

            <div className="rd-modal-body">
              {editSubmissionError ? <div className="rd-alert error">{editSubmissionError}</div> : null}
              {(editSubmission.answers || []).length === 0 ? (
                <div className="rd-empty">Tidak ada jawaban.</div>
              ) : (
                (editSubmission.answers || []).map((a, idx) => (
                  <div className="rd-qa" key={a.id || idx}>
                    <div className="q">
                      {(a.q_order != null ? `${a.q_order}. ` : "")}
                      {a.question_text || `Q${a.q_order}`}
                    </div>
                    <div className="a">
                      {a.answerType === "number" ? (
                        <input
                          type="number"
                          className="rd-edit-input"
                          value={a.editValue}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditSubmission((prev) => ({
                              ...prev,
                              answers: prev.answers.map((x) =>
                                x.id === a.id ? { ...x, editValue: v } : x
                              ),
                            }));
                          }}
                        />
                      ) : (
                        <textarea
                          rows={a.answerType === "json" ? 6 : 3}
                          className="rd-edit-textarea"
                          value={a.editValue}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditSubmission((prev) => ({
                              ...prev,
                              answers: prev.answers.map((x) =>
                                x.id === a.id ? { ...x, editValue: v } : x
                              ),
                            }));
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="rd-modal-foot">
              <button className="rd-btn" onClick={() => !savingEditSubmission && setEditSubmission(null)}>
                Batal
              </button>
              <button className="rd-btn primary" onClick={saveEditedSubmission} disabled={savingEditSubmission}>
                {savingEditSubmission ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

